import React, { useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import { useTranslate } from "react-admin";
import type {
  NotificationTestFormValues,
  WhatsappHeaderMediaType,
} from "../../types/notificationTest";
import {
  WHATSAPP_MEDIA_ACCEPT,
  WHATSAPP_MEDIA_MAX_BYTES,
} from "../../constants";
import { fileToBase64 } from "../../transforms/fileToBase64";

const MEDIA_TYPE_CHOICES: Array<{ id: WhatsappHeaderMediaType; labelKey: string }> = [
  { id: "IMAGE", labelKey: "resources.test.choices.headerMediaType.IMAGE" },
  { id: "DOCUMENT", labelKey: "resources.test.choices.headerMediaType.DOCUMENT" },
  { id: "VIDEO", labelKey: "resources.test.choices.headerMediaType.VIDEO" },
  { id: "AUDIO", labelKey: "resources.test.choices.headerMediaType.AUDIO" },
];

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const TypeIcon: React.FC<{ type: WhatsappHeaderMediaType | "" }> = ({ type }) => {
  if (type === "IMAGE") return <ImageIcon fontSize="small" />;
  if (type === "VIDEO") return <MovieIcon fontSize="small" />;
  if (type === "AUDIO") return <AudiotrackIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
};

// Optional media block shown on the WhatsApp CUSTOM message form. Unlike
// the template-header variant, the operator picks the media type
// themselves and the content field doubles as the media caption — so the
// component has no caption input. When the type is left blank the rest
// of the block stays hidden and the form sends a plain text message.
export const CustomMessageMediaField: React.FC = () => {
  const translate = useTranslate();
  const { control, setValue } =
    useFormContext<NotificationTestFormValues>();

  const type = useWatch({ control, name: "message.customMedia.type" });
  const source = useWatch({ control, name: "message.customMedia.source" });
  const fileName = useWatch({ control, name: "message.customMedia.fileName" });
  const fileBase64 = useWatch({
    control,
    name: "message.customMedia.fileBase64",
  });
  const mimeType = useWatch({ control, name: "message.customMedia.mimeType" });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileSizeRef = useRef<number>(0);
  const errorRef = useRef<string>("");

  const isMediaSelected =
    type === "IMAGE" ||
    type === "DOCUMENT" ||
    type === "VIDEO" ||
    type === "AUDIO";
    
  const isAudio = type === "AUDIO";

  const clearFileState = (): void => {
    setValue("message.customMedia.fileName", "", { shouldDirty: true });
    setValue("message.customMedia.fileBase64", "", { shouldDirty: true });
    setValue("message.customMedia.mimeType", "", { shouldDirty: true });
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileSizeRef.current = 0;
    errorRef.current = "";
  };

  const handleTypeChange = (next: string): void => {
    setValue("message.customMedia.type", next as WhatsappHeaderMediaType | "", {
      shouldDirty: true,
    });
    setValue("message.customMedia.link", "", { shouldDirty: true });
    clearFileState();
  };

  const handleSourceChange = (next: "URL" | "UPLOAD"): void => {
    setValue("message.customMedia.source", next, { shouldDirty: true });
    if (next === "URL") {
      clearFileState();
    } else {
      setValue("message.customMedia.link", "", { shouldDirty: true });
    }
  };

  const handleFilePick = async (file: File | null): Promise<void> => {
    if (!file || !isMediaSelected) return;
    const maxBytes = WHATSAPP_MEDIA_MAX_BYTES[type as WhatsappHeaderMediaType];
    if (file.size > maxBytes) {
      errorRef.current = translate(
        "resources.test.validation.headerMediaTooLarge",
        { max: formatBytes(maxBytes) },
      );
      clearFileState();
      setValue("message.customMedia.fileName", "", { shouldDirty: true });
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      errorRef.current = "";
      fileSizeRef.current = file.size;
      setValue("message.customMedia.fileName", file.name, {
        shouldDirty: true,
      });
      setValue("message.customMedia.fileBase64", base64, { shouldDirty: true });
      setValue(
        "message.customMedia.mimeType",
        file.type || "application/octet-stream",
        { shouldDirty: true },
      );
    } catch {
      errorRef.current = translate(
        "resources.test.validation.headerMediaReadFailed",
      );
      clearFileState();
    }
  };

  const triggerFilePicker = (): void => fileInputRef.current?.click();

  const renderFileSlot = (): React.ReactNode => {
    if (!fileName) {
      return (
        <Box
          onClick={triggerFilePicker}
          sx={{
            border: "1px dashed",
            borderColor: "primary.light",
            borderRadius: 1,
            p: 2.5,
            textAlign: "center",
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { borderColor: "primary.main", color: "text.primary" },
          }}
        >
          <Typography variant="body2">
            {translate("resources.test.fields.headerMediaDropzone")}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {translate("resources.test.fields.headerMediaDropzoneHint", {
              max: formatBytes(
                WHATSAPP_MEDIA_MAX_BYTES[type as WhatsappHeaderMediaType],
              ),
            })}
          </Typography>
        </Box>
      );
    }
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.25,
        }}
      >
        <TypeIcon type={type as WhatsappHeaderMediaType} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileSizeRef.current > 0
              ? `${formatBytes(fileSizeRef.current)} · ${mimeType || ""}`
              : mimeType || ""}
            {fileBase64
              ? ` · ${translate("resources.test.fields.headerMediaReady")}`
              : ""}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={clearFileState} aria-label="remove">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {translate("resources.test.fields.customMediaTitle")}
      </Typography>

      <TextField
        select
        fullWidth
        size="small"
        sx={{ mb: isMediaSelected ? 2 : 0 }}
        label={translate("resources.test.fields.customMediaType")}
        InputLabelProps={{ shrink: true }}
        value={type ?? ""}
        onChange={(e) => handleTypeChange(e.target.value)}
        helperText={
          isAudio
            ? translate("resources.test.fields.customMediaAudioHint")
            : isMediaSelected
              ? translate("resources.test.fields.customMediaCaptionHint")
              : translate("resources.test.fields.customMediaTypeHint")
        }
      >
        <MenuItem value="">
          <em>{translate("resources.test.choices.customMediaType.NONE")}</em>
        </MenuItem>
        {MEDIA_TYPE_CHOICES.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {translate(c.labelKey)}
          </MenuItem>
        ))}
      </TextField>

      {isMediaSelected && (
        <>
          <RadioGroup
            row
            value={source}
            onChange={(e) =>
              handleSourceChange(e.target.value as "URL" | "UPLOAD")
            }
            sx={{ mb: 1.5 }}
          >
            <FormControlLabel
              value="URL"
              control={<Radio size="small" />}
              label={translate("resources.test.fields.headerMediaSourceUrl")}
            />
            <FormControlLabel
              value="UPLOAD"
              control={<Radio size="small" />}
              label={translate("resources.test.fields.headerMediaSourceUpload")}
            />
          </RadioGroup>

          {source === "URL" ? (
            <Controller
              name="message.customMedia.link"
              control={control}
              rules={{
                validate: (v) => {
                  const url = (v ?? "").trim();
                  if (url === "")
                    return translate(
                      "resources.test.validation.headerMediaUrlRequired",
                    );
                  try {
                    const parsed = new URL(url);
                    if (
                      parsed.protocol !== "http:" &&
                      parsed.protocol !== "https:"
                    ) {
                      return translate(
                        "resources.test.validation.headerMediaUrlInvalid",
                      );
                    }
                    return true;
                  } catch {
                    return translate(
                      "resources.test.validation.headerMediaUrlInvalid",
                    );
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  size="small"
                  label={translate("resources.test.fields.headerMediaUrl")}
                  placeholder="https://"
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? " "}
                />
              )}
            />
          ) : (
            <Stack spacing={1.5}>
              {renderFileSlot()}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={
                  WHATSAPP_MEDIA_ACCEPT[type as WhatsappHeaderMediaType]
                }
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  void handleFilePick(file);
                }}
              />
              {!fileName && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={triggerFilePicker}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {translate("resources.test.actions.headerMediaChooseFile")}
                </Button>
              )}
              {errorRef.current && (
                <Typography variant="caption" color="error">
                  {errorRef.current}
                </Typography>
              )}
              {type === "DOCUMENT" && (
                <Controller
                  name="message.customMedia.fileName"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (v ?? "").trim() !== "" ||
                      translate(
                        "resources.test.validation.headerMediaFilenameRequired",
                      ),
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      size="small"
                      label={translate(
                        "resources.test.fields.headerMediaFilename",
                      )}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ??
                        translate(
                          "resources.test.fields.headerMediaFilenameHint",
                        )
                      }
                    />
                  )}
                />
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomMessageMediaField;
